import React, { useState, useEffect, useCallback } from "react";
import { DEFAULT_LOGO as RlinkWhiteLogo } from "../../config/defaultLogo";
import { useSiteContext } from "../../context/SiteContext";
const WebinerWaiting = () => {
  const { isAuthenticated, user, meetingSettings, setMeetingSettings } =
    useSiteContext();
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [isChatOpen, setIsChatOpen] = useState(false);

  const calculateTimeDifference = useCallback(() => {
    if (!meetingSettings?.scheduledStartTime) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    try {
      // Parse the scheduled meeting time (ISO string in UTC)
      let meetingTimeUTC = new Date(meetingSettings.scheduledStartTime);

      // Add countdown minutes if specified
      if (meetingSettings.countdownMinutes) {
        meetingTimeUTC = new Date(
          meetingTimeUTC.getTime() +
            meetingSettings.countdownMinutes * 60 * 1000
        );
      }

      // Get the current time
      // JavaScript Date objects store time in UTC internally
      // getTime() returns UTC milliseconds, so we can directly compare
      const now = new Date();

      // Calculate the difference in milliseconds
      // Both timestamps are in UTC internally, so the difference is accurate
      // This automatically accounts for the browser's timezone
      const diffMs = meetingTimeUTC.getTime() - now.getTime();

      // If the meeting has already passed, return zeros
      if (diffMs <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      // Convert milliseconds to days, hours, minutes, seconds
      const totalSec = Math.floor(diffMs / 1000);
      const days = Math.floor(totalSec / (3600 * 24));
      const hours = Math.floor((totalSec % (3600 * 24)) / 3600);
      const minutes = Math.floor((totalSec % 3600) / 60);
      const seconds = totalSec % 60;

      return { days, hours, minutes, seconds };
    } catch (err) {
      console.error("Error calculating time difference:", err);
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
  }, [meetingSettings?.scheduledStartTime, meetingSettings?.countdownMinutes]);

  useEffect(() => {
    // Calculate initial time difference
    setTimeLeft(calculateTimeDifference());

    // Update countdown every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeDifference());
    }, 1000);

    return () => clearInterval(timer);
  }, [calculateTimeDifference]);

  const formatTime = (num) => String(num).padStart(2, "0");

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: "linear-gradient(159deg, #000 29.02%, #3A285E 82.31%)",
      }}
    >
      {/* Header */}
      <header className="flex justify-between items-center h-[60px] px-4 md:px-8 border-b border-[#2B2A3C] bg-black">
        <div className="flex items-center">
          <svg
            width="35"
            height="32"
            viewBox="0 0 35 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.8881 17.545C26.9885 17.1158 28.7936 12.5266 26.8103 9.19201C24.7489 5.72537 20.147 6.91393 16.7932 6.77086C16.2695 4.15162 15.0326 2.03862 12.8376 0.464879C16.8712 0.894082 21.0384 0.299801 25.072 0.773025C29.4956 1.27926 32.4372 3.0291 33.3175 7.55224C34.0306 11.261 33.9749 17.512 30.8216 20.1532C30.4761 20.4393 29.0833 21.1437 29.139 21.3638L35 31.2905L26.632 31.2244L21.2724 22.3212H16.7932L16.6483 17.622C16.8377 17.2918 16.9603 17.545 17.016 17.545C18.8211 17.545 21.161 17.7211 22.8881 17.545Z"
              fill="white"
            />
            <path
              d="M15.1886 25.7218C14.3195 33.2934 2.61989 34.438 0.413675 26.8334C-0.121165 24.9845 -0.165734 18.0292 0.447103 16.2353C1.61707 12.8457 5.32752 10.6667 8.92655 11.349C9.81795 11.5141 9.5171 14.0563 9.09368 14.6396C8.16886 15.9272 5.18267 15.52 4.80382 18.0842C4.60326 19.4819 4.58097 23.873 4.78154 25.2706C5.08238 27.4937 8.32485 28.231 9.84023 26.6573C10.9768 25.4687 10.4754 23.0806 10.7093 21.5839L13.2053 20.1312L15.1886 17.9632C14.9881 20.4173 15.4672 23.3007 15.1886 25.6998V25.7218Z"
              fill="white"
            />
            <path
              d="M5.94036 17.9081C5.94036 16.7086 10.0408 16.4995 10.4531 14.2544C10.7428 12.7027 10.7985 8.91688 10.6202 7.35414C10.3639 5.1421 8.30257 3.94253 6.23006 4.87797C4.04613 5.85743 5.01553 8.61974 4.6924 10.4576C2.74246 11.261 1.4165 12.5156 0.0682592 14.0783V6.48473C0.0682592 3.44729 3.83442 0.343822 6.7649 0.0576863C15.3892 -0.789715 15.6789 7.88239 15.1886 14.0123C14.8098 18.7996 10.5868 21.4738 5.94036 20.7365V17.9081Z"
              fill="white"
            />
          </svg>
        </div>

        <div className="flex items-center gap-2">
          {!isAuthenticated && (
            <span className="text-white text-xs font-montserrat hidden md:block">
              Sign Up
            </span>
          )}
          {isAuthenticated && (
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 0C4.47518 0 0 4.47519 0 10C0 12.1565 0.686968 14.1508 1.85109 15.7824C3.66407 18.3302 6.63167 20 10 20C13.3683 20 16.3359 18.3302 18.1489 15.7824C19.313 14.1508 20 12.1565 20 10C20 4.47519 15.5248 0 10 0ZM6.5649 7.7958C6.5649 5.89695 8.10114 4.36069 10 4.36069C11.8989 4.36069 13.4351 5.89695 13.4351 7.7958C13.4351 7.80535 13.4351 7.81489 13.4351 7.83397C13.4351 7.85305 13.4351 7.85305 13.4351 7.87214C13.4351 9.77099 11.8989 11.3073 10 11.3073C8.10114 11.3073 6.5649 9.77099 6.5649 7.87214C6.5649 7.8626 6.5649 7.85305 6.5649 7.83397C6.5649 7.81489 6.5649 7.81489 6.5649 7.7958ZM16.813 14.8855C15.2863 17.0038 12.8053 18.3969 10 18.3969C7.19465 18.3969 4.71372 17.0134 3.187 14.8855C3.19654 14.8855 3.21566 14.876 3.2252 14.8664C3.21566 14.8473 3.19654 14.8378 3.187 14.8187C5.18127 13.6355 7.50954 12.9485 10 12.9485C12.4905 12.9485 14.8187 13.6355 16.813 14.8187C16.8035 14.8378 16.7843 14.8473 16.7748 14.8664C16.7843 14.8664 16.8035 14.876 16.813 14.8855Z"
                fill="#7F7F7F"
              />
            </svg>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-between px-6 md:px-12 py-8 md:py-10 relative">
        <div className="flex flex-col items-center gap-6 md:gap-10 w-full max-w-2xl">
          {/* Kim Garst Logo */}
          {/* <div className="flex flex-col items-center">
            <img
              src="/kim-garst.png"
              alt="Kim Garst"
              className="h-16 md:h-20 w-auto object-contain"
            />
            <p className="text-white text-[8px] md:text-[10px] font-montserrat font-medium text-center mt-1 tracking-wider">
              BUSINESS MENTORING
            </p>
          </div> */}

          {/* Summit Title */}
          <div className="flex flex-col items-center">
            <h1 className="text-white text-2xl md:text-[33px] font-montserrat font-medium text-center leading-tight">
              {meetingSettings && meetingSettings.registrationTitle
                ? meetingSettings.registrationTitle
                : meetingSettings.roomName}
            </h1>
            {meetingSettings && meetingSettings.registrationSubtitle && (
              <p
                className="text-white text-center font-bold text-2xl lg:text-[26px] leading-tight"
                style={{ fontFamily: "'Open Sans', sans-serif" }}
              >
                {meetingSettings.registrationSubtitle}
              </p>
            )}
          </div>

          {/* Center Image */}
          <div className="w-full flex justify-center max-w-[538px]">
            {meetingSettings && meetingSettings.waitingRoomCoverImage ? (
              <img
                className="w-auto h-[350px]"
                src={meetingSettings.waitingRoomCoverImage}
                alt=""
              />
            ) : (
              <img className="w-auto h-[350px]" src={"/webinarlogo2.png"} />
            )}
          </div>

          {/* Countdown Timer */}
          {meetingSettings?.countdownTimer && (
            <>
              <div className="flex flex-col items-center gap-3 md:gap-4">
                <h2 className="text-[#D45EDB] text-xl md:text-[25px] font-montserrat font-extrabold text-center tracking-wide">
                  STARTING IN
                </h2>
                <div className="text-[#D45EDB] text-4xl md:text-[48px] font-montserrat font-extrabold text-center leading-none">
                  {meetingSettings?.scheduledStartTime ? (
                    <>
                      {timeLeft.days > 0 ||
                      timeLeft.hours > 0 ||
                      timeLeft.minutes > 0 ||
                      timeLeft.seconds > 0 ? (
                        <>
                          {timeLeft.days > 0 && <>{timeLeft.days}d </>}
                          {formatTime(timeLeft.hours)}:
                          {formatTime(timeLeft.minutes)}:
                          {formatTime(timeLeft.seconds)}
                        </>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <span className="text-[#D45EDB] text-2xl md:text-3xl font-montserrat font-bold">
                            00:00:00
                          </span>
                          <span className="text-[#D45EDB] text-sm md:text-base font-montserrat">
                            Please wait while we prepare the webinar
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    "00:00:00"
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 mt-8">
          <span className="text-white text-[7px] md:text-[8px] font-medium tracking-wider">
            POWERED BY
          </span>
          <img className="w-20" src={RlinkWhiteLogo} alt="" />
        </div>

        {/* Chat Button - Fixed Position */}
        {/* <button
          onClick={() => setIsChatOpen(!isChatOpen)}
          className="fixed right-4 md:right-8 top-[80px] md:top-[90px] w-12 h-12 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors z-40"
          aria-label={isChatOpen ? "Close chat" : "Open chat"}
        >
          {isChatOpen ? (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#7F7F7F"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              width="19"
              height="18"
              viewBox="0 0 19 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.4022 0H1.83021C0.820068 0 0 0.797616 0 1.78004V17.8004L3.53039 14.1236H16.3921C17.4023 14.1236 18.2225 13.326 18.2225 12.3436V1.78004C18.2225 0.797616 17.4023 0 16.3921 0H16.4022ZM11.1015 10.7483H3.56042V8.87103H11.1015V10.7483ZM14.6719 7.9956H3.56042V6.11829H14.6719V7.9956ZM14.6719 5.39849H3.56042V3.52118H14.6719V5.39849Z"
                fill="#7F7F7F"
              />
            </svg>
          )}
        </button> */}

        {/* Chat Panel */}
        {/* {isChatOpen && (
          <div className="fixed right-4 md:right-8 top-[140px] min-h-96 md:top-[150px] w-full max-w-[348px] h-auto max-h-[calc(100vh-180px)] md:max-h-[703px] bg-black/40 backdrop-blur-sm rounded-lg flex flex-col p-4 md:px-[22px] md:py-[10px] gap-4 z-30 shadow-2xl overflow-y-auto">
            {chatMessages.map((msg) => (
              <div key={msg.id} className="flex items-end gap-2.5 w-full">
                <div className="flex-shrink-0 w-[34px] h-[34px] rounded-full bg-purple-750 flex items-center justify-center">
                  <span className="text-white font-poppins text-[17px] font-normal leading-normal tracking-[0.34px]">
                    {msg.initial}
                  </span>
                </div>

                <div className="flex-1 min-w-0 flex flex-col gap-1.5 bg-[#242626] rounded-[10px] px-2.5 py-1.5">
                  <div className="flex justify-between items-center w-full gap-2">
                    <span className="text-purple-750 font-poppins text-[10px] font-semibold leading-normal tracking-[0.2px]">
                      {msg.name}
                    </span>
                    <span className="text-[#999] font-poppins text-[9px] font-normal leading-normal tracking-[0.17px] whitespace-nowrap">
                      {msg.time}
                    </span>
                  </div>

                  <p className="text-white font-poppins text-xs font-normal leading-normal tracking-[0.24px] break-words">
                    {msg.message}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )} */}
      </main>
    </div>
  );
};

export default WebinerWaiting;
